import {Keyboard, KeyboardInfo} from '@capacitor/keyboard';

/**
 * Ionic only installs its scroll-assist shim on iOS (see `needInputShims()` in
 * @ionic/core): on Android it assumes the native webview shrinks when the keyboard
 * opens, so the webview itself scrolls the focused field into view.
 *
 * Capacitor 8 does that resize from the SystemBars plugin, which pads the webview's
 * parent with the IME inset. That works on stock Android, but it depends on the OS
 * and Android System WebView versions, and keyboards that grow after the resize
 * (the Samsung suggestion/toolbar row) leave the field under the keyboard because
 * nothing scrolls a second time.
 *
 * This assist only does something when the keyboard actually overlaps the layout
 * viewport. When the native resize did its job the overlap is 0 and every call
 * below is a no-op, so it stays out of Ionic's way on the devices that work.
 */

// The webview resize and the reported keyboard height never match to the pixel
// (the gesture bar inset disappears at the same time), so ignore small leftovers.
const OVERLAP_TOLERANCE = 24;

// Breathing room between the field and the top of the keyboard.
const FIELD_MARGIN = 12;

// Viewport height with no keyboard up, to measure the native resize against. The
// webview is already resized by the time `keyboardWillShow` fires, so this has to
// be captured earlier: focus always lands on the field before the keyboard opens.
let restingViewport = 0;
let restingViewportFrozen = false;

let keyboardVisible = false;
let offsetContent: HTMLIonContentElement | null = null;

const focusedField = (): HTMLElement | null => {
  let el = document.activeElement as HTMLElement | null;
  while (el?.shadowRoot?.activeElement) {
    el = el.shadowRoot.activeElement as HTMLElement;
  }
  if (!el) {
    return null;
  }
  const editable = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
  return editable ? el : null;
};

const clearOffset = () => {
  offsetContent?.style.removeProperty('--keyboard-offset');
  offsetContent = null;
};

const setOffset = (content: HTMLIonContentElement | null, overlap: number) => {
  if (offsetContent && offsetContent !== content) {
    clearOffset();
  }
  if (!content) {
    return;
  }
  content.style.setProperty('--keyboard-offset', `${overlap}px`);
  offsetContent = content;
};

const revealField = async(info: KeyboardInfo) => {
  const field = focusedField();
  if (!field) {
    return;
  }

  // How much of the keyboard the webview did not already take off the viewport.
  const resizedBy = Math.max(0, restingViewport - window.innerHeight);
  const overlap = info.keyboardHeight - resizedBy;
  if (overlap < OVERLAP_TOLERANCE) {
    clearOffset();
    return;
  }

  // Padding first, so the content can actually scroll far enough.
  const content = field.closest('ion-content');
  setOffset(content, overlap);

  const hiddenBy = field.getBoundingClientRect().bottom + FIELD_MARGIN - (window.innerHeight - overlap);
  if (hiddenBy <= 0) {
    return;
  }

  if (content) {
    const scrollEl = await content.getScrollElement();
    scrollEl.scrollBy({top: hiddenBy, behavior: 'smooth'});
  } else {
    field.scrollIntoView({block: 'center', behavior: 'smooth'});
  }
};

/**
 * Android only — on iOS Ionic's own scroll assist already handles this, and running
 * both would scroll the field twice.
 */
export const startAndroidKeyboardAssist = async() => {
  restingViewport = window.innerHeight;

  window.addEventListener('resize', () => {
    if (!restingViewportFrozen && !keyboardVisible) {
      restingViewport = window.innerHeight;
    }
  });

  // Stop tracking as soon as a field is focused: the next resize is the keyboard's.
  document.addEventListener('focusin', () => {
    if (focusedField()) {
      restingViewportFrozen = true;
    }
  });

  document.addEventListener('focusout', () => {
    if (!keyboardVisible) {
      restingViewportFrozen = false;
    }
  });

  await Keyboard.addListener('keyboardWillShow', () => {
    keyboardVisible = true;
  });

  // `keyboardDidShow` fires again whenever the keyboard changes size, which is how
  // a late-appearing OEM toolbar row gets handled.
  await Keyboard.addListener('keyboardDidShow', (info) => {
    revealField(info);
  });

  await Keyboard.addListener('keyboardDidHide', () => {
    keyboardVisible = false;
    restingViewportFrozen = false;
    restingViewport = window.innerHeight;
    clearOffset();
  });
};
